import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import { useTracking, getLabel, useId, toReactProps } from '../utils';
import { InputGroupContext } from './utils';
import { Input, HelpText, Label } from '../';
import type { TrackingProp } from '../types';
import type { InputProps } from '../input/input';
import type { HelpTextProps, LabelProps } from '../';
import styles from './input-group.module.css';
import clsx from 'clsx';

export type InputGroupProps = Omit<
  React.ComponentPropsWithoutRef<'fieldset'>,
  'onChange'
> & {
  'data-theme'?: string;
  defaultValue?: InputProps['value'];
  disabled?: boolean;
  invalid?: boolean;
  internal?: 'small' | false;
  name: string;
  onChange?: (values: string[]) => void;
  required?: boolean;
  tracking?: TrackingProp;
  type?: 'radio' | 'checkbox';
  value?: InputProps['value'];
  variant?: 'row' | 'row-background' | 'column' | 'column-background';
};

export type InputGroupInputProps = Omit<InputProps, 'wrapperProps'> & {
  label: React.ReactNode | Omit<LabelProps, 'htmlFor'>;
  helpText?: React.ReactNode | Omit<HelpTextProps, 'htmlFor'>;
  wrapperProps?: React.ComponentPropsWithoutRef<'div'>;
  graphic?: React.ReactNode;
};

const TRACK_VALUES = '@values'; // Use input values in "event_status" tracking

const InputGroupComp = forwardRef<HTMLFieldSetElement, InputGroupProps>(
  function InputGroup(
    {
      children,
      className,
      defaultValue,
      disabled,
      invalid,
      internal,
      name,
      onChange,
      required,
      tracking = true,
      type = 'radio',
      value,
      variant = 'row',
      ...rest
    },
    ref
  ) {
    // Support both value (controlled) and defaultValue (uncontrolled)
    // Read more on https://react.dev/reference/react-dom/components/input#optimizing-re-rendering-on-every-keystroke
    const [defaults, setDefaults] = useState(defaultValue);
    const values = toStrings(value ?? defaults);
    const innerRef = useRef<HTMLFieldSetElement>(null);
    const classes = clsx(
      styles.inputGroup,
      styles[variant],
      className,
      internal && styles[internal]
    );

    const track = useTracking(tracking);
    const handleChange = ({
      target: input,
      currentTarget: group
    }: React.ChangeEvent<HTMLFieldSetElement>) => {
      if (!(input instanceof HTMLInputElement) || input.name !== name) return; // Only track inputs with same name the <InputGroup>
      const legend = group.querySelector('legend') as HTMLLegendElement;
      const checked = group.querySelectorAll<HTMLInputElement>('input:checked');
      const labels = Array.from(checked, getLabel);
      const values = Array.from(checked, ({ value }) => value);
      const status = typeof tracking === 'object' && tracking.eventStatus;
      const overwrite = {};

      // If eventStatus is default or set to TRACK_VALUES
      if (!status || status === TRACK_VALUES) {
        const items = status === TRACK_VALUES ? values : labels;
        Object.assign(overwrite, { eventStatus: items.sort().join('_') });
      }

      setDefaults(values);
      track(
        {
          eventCategory: `inputgroup_${type}`,
          eventAction: input.checked ? 'checked' : 'unchecked',
          eventLabel: getLabel(legend)
        },
        overwrite
      );
      onChange?.(values); // Track before onChange in case URL changes
    };

    const useGroupProps = (props: InputProps) => {
      if (props.name && props.name !== name) return; // Only take control if input name is missing or same as InputGroup name
      props['aria-invalid'] = props.invalid || invalid;
      props.checked = values.includes(String(props.value));
      props.name = name;
      props.onChange = props.onChange || (() => {});
      props.required = props.required || required;
      props.disabled = props.disabled || disabled;
      props.tracking = false; // Let input group do the tracking
      props.type = props.type || type;
      props.internal = internal;
    };

    useImperativeHandle(ref, () => innerRef.current as HTMLFieldSetElement); // Forward innerRef
    useEffect(() => {
      if (innerRef.current?.querySelector('legend')) return;
      console.warn('InputGroup is missing <Legend> element', innerRef.current);
    }, []);

    return (
      <fieldset
        {...rest}
        className={classes}
        onChange={handleChange}
        ref={innerRef}
      >
        <InputGroupContext.Provider value={useGroupProps}>
          {children}
        </InputGroupContext.Provider>
      </fieldset>
    );
  }
);

const InputGroupInput = forwardRef<HTMLDivElement, InputGroupInputProps>(
  function InputGroupInput(
    { helpText, label, graphic, wrapperProps, ...rest },
    ref
  ) {
    const ariaId = useId();
    const inputId = rest.id || ariaId || '';
    const classes = clsx(styles.inputGroupItem, wrapperProps?.className);
    const labelProps = toReactProps<LabelProps>(label);
    const helpTextProps = toReactProps<HelpTextProps>(helpText);

    return (
      <div ref={ref} className={classes}>
        {!!graphic && <div className={styles.graphic}>{graphic}</div>}
        <Input {...rest} id={inputId} />
        <Label {...labelProps} htmlFor={inputId} />
        {!!helpText && (
          <HelpText
            {...helpTextProps}
            className={clsx(styles.helpText, helpTextProps?.className)}
            htmlFor={inputId}
          />
        )}
      </div>
    );
  }
);

export const InputGroup = Object.assign(InputGroupComp, {
  TRACK_VALUES,
  Input: InputGroupInput
});

const toStrings = (value: unknown) =>
  (Array.isArray(value) ? value : [value]).map(String);
